import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { checkTrialStatusLogic } from '../utils/general'
import { hasPreviewModeProject, isUserFromEligibleCountry, isRlTrialOrigin } from '../utils/free-trial'
import DEEP_LINK from '../utils/deepLinks'

const useFreeTrialModal = (
  userProfile,
  liveSites,
  dispatch,
  setFirstTimeSuccModal,
  shouldShowOnUploadUpdate,
  previewData
) => {
  const [showModal, setShowModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [planType, setPlanType] = useState('pro')
  const [hasUsedTrial, setHasUsedTrial] = useState(null)
  const [isLoadingTrialStatus, setIsLoadingTrialStatus] = useState(false)
  const history = useHistory()
  const isInPreviewMode = hasPreviewModeProject(liveSites, previewData)

  useEffect(() => {
    if (isInPreviewMode) {
      setShowModal(false)
      setShowPaymentModal(false)
    }
  }, [isInPreviewMode])

  useEffect(() => {
    const checkTrialStatusFromAPI = async () => {
      if (!userProfile?.email) return

      setIsLoadingTrialStatus(true)
      const result = await checkTrialStatusLogic(userProfile)
      setHasUsedTrial(result?.hasUsedTrial)
      setIsLoadingTrialStatus(false)
    }

    checkTrialStatusFromAPI()
  }, [userProfile?.email, userProfile?.hasUsedTrial])

  useEffect(() => {
    if (shouldShowOnUploadUpdate || isInPreviewMode) {
      return
    }

    const currentPath = window.location.pathname
    const deepLinkPaths = Object.values(DEEP_LINK)
    const isNonManageDeepLink = currentPath !== DEEP_LINK.MANAGE && deepLinkPaths.includes(currentPath)

    // Do not show free trial modal on deeplink routes.
    if (isNonManageDeepLink) {
      return
    }

    if (!userProfile || !userProfile.email || userProfile?.productId || hasUsedTrial === null || isLoadingTrialStatus) {
      return
    }

    const { userCategory, productId } = userProfile
    const isTrialAccount = !productId
    const isB2BUser = userCategory === 'B2B'
    const isB2CUser = userCategory === 'B2C'
    const isFromEligibleCountry = isUserFromEligibleCountry() || isRlTrialOrigin()

    const shouldShowModalB2B = isTrialAccount && isB2BUser
    const shouldShowModalB2C = isB2CUser && isFromEligibleCountry && isTrialAccount

    const hasCompletedTrial = localStorage.getItem('hasCompletedFreeTrial') === 'true' || hasUsedTrial

    const hasSkippedTrialB2C =
      !shouldShowOnUploadUpdate &&
      isB2CUser &&
      isFromEligibleCountry &&
      sessionStorage.getItem('hasSkippedFreeTrialB2C') === 'true'

    if (hasCompletedTrial || hasSkippedTrialB2C) {
      return
    }

    if (shouldShowModalB2B) {
      setPlanType('pro')
      setShowModal(true)
      history.replace({ pathname: '/manage/free-trial' })
      return
    }

    if (shouldShowModalB2C) {
      setPlanType('solo')

      setShowModal(true)
      history.replace({ pathname: '/manage/free-trial' })
    }
  }, [
    userProfile?.email,
    userProfile?.productId,
    userProfile?.trialStatus,
    liveSites,
    isInPreviewMode,
    hasUsedTrial,
    isLoadingTrialStatus,
    shouldShowOnUploadUpdate
  ])

  const handleStartTrial = async () => {
    setShowModal(false)
    setShowPaymentModal(true)
  }

  const handleSkipTrial = () => {
    const isFirstTimeUpload = localStorage.getItem('isFirstTimeUpload') === 'true'

    localStorage.setItem('freeTrialModalDismissedForUpload', 'true')

    setShowModal(false)

    if (shouldShowOnUploadUpdate) {
      return
    }

    if (isFirstTimeUpload && liveSites && liveSites.length === 1) {
      const firstSite = liveSites[0]
      dispatch(setFirstTimeSuccModal({ showFistTimeSuccModal: true, firstTimeSite: firstSite }))
    }
    localStorage.removeItem('isFirstTimeUpload')

    history.replace({ pathname: '/manage' })
  }

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false)
    localStorage.setItem('hasSkippedFreeTrial', 'true')
  }

  return {
    showModal,
    setShowModal,
    showPaymentModal,
    setShowPaymentModal,
    handleStartTrial,
    handleSkipTrial,
    handlePaymentModalClose,
    planType,
    setPlanType,
    preventSkip: isRlTrialOrigin()
  }
}

export default useFreeTrialModal
